import React, { useState, useEffect, useMemo } from 'react';
import Plot from 'react-plotly.js';
import { atlasService } from '../../services/atlasService';
import MacroRegimePage from '../trading/MacroRegimePage';
import { mergeLayout, PLOTLY_CONFIG } from '../../components/charts/chartTheme';

// Atlas — the macro warehouse read surface (superuser-only). Two modes:
//   Dashboard — the curated "signal" grid: derived indicators + key raws, each a card with a sparkline,
//               change chip and percentile heat (how extreme vs its own history).
//   Browse    — the full catalog: buckets → series → point-in-time chart + component tree.
// Dashboard/Browse = RAW values. Regime (third tab, folded in 2026-09-23 from /trading/macro)
// = the downstream detector layer, served day-by-day from MacroRegimeSnapshot.

const isDark = () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
const GOLD = 'rgb(var(--brand-gold-rgb))';
const UP = '#10b981';
const DOWN = '#ef4444';
const NEUTRAL = '#9ca3af';
const TONE = { pos: UP, neg: DOWN, neutral: NEUTRAL };

// Real US recessions (NBER peak→trough) — static overlay shading; changes ~once a decade, no live dep.
const NBER = [
  ['1980-01-01', '1980-07-01'], ['1981-07-01', '1982-11-01'], ['1990-07-01', '1991-03-01'],
  ['2001-03-01', '2001-11-01'], ['2007-12-01', '2009-06-01'], ['2020-02-01', '2020-04-01'],
];

// economic-direction tone: a widening HY spread / rising VIX reads RED even though its number went up.
function toneFor(change, polarity) {
  if (change == null || change === 0) return 'neutral';
  const up = change > 0;
  if (polarity === 'good') return up ? 'pos' : 'neg';
  if (polarity === 'bad') return up ? 'neg' : 'pos';
  return 'neutral';   // pure levels (rates, indices) carry no good/bad
}
const shortDate = (iso) => (iso ? new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) : '');

const BUCKET_LABEL = {
  rates: 'Rates & Curve', liquidity: 'Liquidity', inflation: 'Inflation', growth: 'Growth & Cycle',
  labor: 'Labor', credit: 'Credit & Conditions', markets: 'Market Risk', commodities: 'Commodities',
  global: 'Global', other: 'Other',
};
const USE_LABEL = { R: 'Regime', T: 'Tilt', P: 'PEAD' };

// ── value formatting (unit-aware) ─────────────────────────────────────────────────────────────────
const _compact = (v) => {
  const a = Math.abs(v);
  if (a >= 1e12) return `${(v / 1e12).toFixed(2)}T`;
  if (a >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
};
function fmtValue(v, units, { signed = false } = {}) {
  if (v == null || isNaN(v)) return '—';
  const u = (units || '').toLowerCase();
  const sgn = signed && v > 0 ? '+' : '';
  if (u.includes('percent')) return `${sgn}${v.toFixed(2)}%`;
  if (u.includes('std dev')) return `${sgn}${v.toFixed(2)}σ`;
  if (u.includes('thousand')) return `${sgn}${_compact(v * 1e3)}`;      // stored in thousands
  if (u.includes('million')) return `${sgn}${_compact(v * 1e6)}`;      // stored in millions ($)
  if (u.includes('billion')) return `${sgn}${_compact(v * 1e9)}`;
  return `${sgn}${v.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

// ── inline SVG sparkline (area + line + endpoint dot) ─────────────────────────────────────────────
function Sparkline({ data, color, w = 148, h = 40 }) {
  if (!data || data.length < 2) return <div style={{ height: h }} />;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const rng = max - min || 1;
  const X = (i) => (i / (data.length - 1)) * w;
  const Y = (v) => h - 2 - ((v - min) / rng) * (h - 4);
  const line = data.map((v, i) => `${X(i).toFixed(1)},${Y(v).toFixed(1)}`).join(' ');
  const area = `0,${h} ${line} ${w},${h}`;
  const lx = X(data.length - 1);
  const ly = Y(data[data.length - 1]);
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" role="img" aria-label="sparkline">
      <polygon points={area} fill={color} opacity="0.10" />
      <polyline points={line} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke"
        strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lx} cy={ly} r="2.4" fill={color} />
    </svg>
  );
}

// ── percentile heat track (where the latest sits within its own history) ──────────────────────────
function PctBar({ pctile }) {
  if (pctile == null) return null;
  // cool (low) → warm (high); extremes read as "notable" either way
  const hue = 210 - (pctile / 100) * 180;   // 210 (blue) → 30 (amber)
  return (
    <div className="flex items-center gap-1.5" title={`Latest is at the ${pctile}th percentile of its history`}>
      <div className="relative h-1 flex-1 rounded-full bg-gray-200 dark:bg-gray-700">
        <div className="absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full ring-2 ring-white dark:ring-[#0d1117]"
          style={{ left: `calc(${pctile}% - 5px)`, backgroundColor: `hsl(${hue} 70% 50%)` }} />
      </div>
      <span className="tabular-nums text-[10px] text-gray-400">P{pctile}</span>
    </div>
  );
}

function Badge({ children, tone = 'gray' }) {
  const t = {
    gray: 'bg-gray-500/10 text-gray-500 dark:text-gray-400',
    gold: 'bg-brand-gold/10 text-brand-gold',
    amber: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  };
  return <span className={`rounded px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide ${t[tone]}`}>{children}</span>;
}

// ── one dashboard card ────────────────────────────────────────────────────────────────────────────
function Card({ c, onOpen }) {
  const tone = toneFor(c.change, c.polarity);
  const chClass = tone === 'pos' ? 'text-emerald-500' : tone === 'neg' ? 'text-red-500' : 'text-gray-400';
  const held = (c.change == null || c.change === 0) && c.held_since;
  const arrow = tone === 'neutral' || !c.change ? '' : c.change > 0 ? '▲' : '▼';
  return (
    <button onClick={() => onOpen(c.code, c.frequency)}
      className="group flex flex-col rounded-xl border border-gray-200 bg-white p-3 text-left transition-all hover:border-brand-gold hover:shadow-sm dark:border-gray-800 dark:bg-[#161b22]">
      <div className="mb-1 flex items-center justify-between gap-1">
        <span className="truncate font-mono text-[11px] text-gray-500 dark:text-gray-400">{c.code}</span>
        <span className="flex shrink-0 items-center gap-1">
          {c.stale && <Badge tone="amber">Stale</Badge>}
          {c.derived && <Badge tone="gold">Derived</Badge>}
          <Badge>{c.frequency}</Badge>
        </span>
      </div>
      <div className="mb-1 truncate text-[11px] text-gray-500 dark:text-gray-400" title={c.title}>{c.title}</div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-serif text-xl tabular-nums text-gray-900 dark:text-gray-100">{c.value_display}</span>
        {held
          ? <span className="shrink-0 text-[10px] text-gray-400">held since {shortDate(c.held_since)}</span>
          : <span className={`shrink-0 text-[11px] tabular-nums ${chClass}`}>{arrow} {c.change_display}</span>}
      </div>
      <div className="my-1.5"><Sparkline data={c.spark} color={TONE[tone]} /></div>
      <PctBar pctile={c.pctile} />
      <div className="mt-1 truncate text-[10px] text-gray-400">{c.units} · {c.latest_date}</div>
    </button>
  );
}

// ── shared chart builders (Browse detail + expand modal) ─────────────────────────────────────────
function chartTrace(obs, unitKind) {
  if (!obs || !obs.length) return null;
  const k = unitKind || 'num';
  const scale = k === 'usd_m' ? 1e6 : k === 'usd_b' ? 1e9 : k === 'thousand' ? 1e3 : 1;   // plot real $
  const ht = k === 'pct' ? '%{x|%b %Y}: %{y:.2f}%<extra></extra>'
    : k === 'sigma' ? '%{x|%b %Y}: %{y:+.2f}σ<extra></extra>'
    : (k === 'usd_m' || k === 'usd_b') ? '%{x|%b %Y}: $%{y:.3s}<extra></extra>'
    : k === 'thousand' ? '%{x|%b %Y}: %{y:.3s}<extra></extra>'
    : '%{x|%b %Y}: %{y:.2f}<extra></extra>';
  return [{ x: obs.map((p) => p.date), y: obs.map((p) => (p.value == null ? null : p.value * scale)),
    type: 'scatter', mode: 'lines', line: { color: GOLD, width: 1.6 }, connectgaps: false, hovertemplate: ht }];
}
function nberShapes(obs, theme) {
  if (!obs || !obs.length) return [];
  const lo = obs[0].date; const hi = obs[obs.length - 1].date;
  return NBER.filter(([a, b]) => b >= lo && a <= hi).map(([a, b]) => ({
    type: 'rect', xref: 'x', yref: 'paper', x0: a < lo ? lo : a, x1: b > hi ? hi : b, y0: 0, y1: 1,
    fillcolor: theme === 'dark' ? 'rgba(148,163,184,0.12)' : 'rgba(100,116,139,0.10)', line: { width: 0 }, layer: 'below',
  }));
}
const RANGE_KEYS = ['1M', '3M', '6M', 'YTD', '1Y', '5Y', '10Y', 'Max'];
const FREQ_MIN_RANGE = { Q: '1Y', M: '3M', A: '5Y' };   // don't offer ranges a series cadence can't fill
function rangeStart(k) {
  if (k === 'Max') return '';
  const now = new Date();
  if (k === 'YTD') return `${now.getUTCFullYear()}-01-01`;
  const days = { '1M': 30, '3M': 91, '6M': 182, '1Y': 365, '5Y': 1825, '10Y': 3650 }[k];
  return new Date(now.getTime() - days * 86400000).toISOString().slice(0, 10);
}

// ── expand-to-chart modal: card → real chart with a period rail (default 1Y, never Max) ──────────
function ChartModal({ code, freq, onClose }) {
  const [range, setRange] = useState('1Y');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = isDark() ? 'dark' : 'light';
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useEffect(() => {
    if (!code) return undefined;
    let alive = true; setLoading(true);
    atlasService.getSeries(code, range === 'Max' ? {} : { start: rangeStart(range) })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [code, range]);
  const obs = useMemo(() => data?.observations || [], [data]);
  const trace = useMemo(() => chartTrace(obs, data?.unit_kind), [obs, data]);
  const shapes = useMemo(() => nberShapes(obs, theme), [obs, theme]);
  const last = obs.length ? obs[obs.length - 1] : null;
  const minIdx = FREQ_MIN_RANGE[freq] ? RANGE_KEYS.indexOf(FREQ_MIN_RANGE[freq]) : 0;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-4xl rounded-xl border border-gray-200 bg-white p-4 shadow-2xl dark:border-gray-800 dark:bg-[#0d1117]" onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm text-brand-gold">{code}</span>
              {loading && <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-brand-gold/70 border-t-transparent" />}
            </div>
            <h3 className="truncate font-serif text-lg text-gray-900 dark:text-gray-100">{data?.title || ''}</h3>
            <div className="text-[11px] text-gray-400">{data?.units}{last ? ` · latest ${fmtValue(last.value, data?.units)} (${last.date})` : ''}</div>
          </div>
          <button onClick={onClose} title="Close (Esc)" className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800">✕</button>
        </div>
        <div className="mb-2 flex flex-wrap gap-1">
          {RANGE_KEYS.map((k, i) => {
            const disabled = i < minIdx && k !== 'Max';
            return (
              <button key={k} disabled={disabled} onClick={() => setRange(k)}
                className={`rounded px-2.5 py-1 text-[11px] ${range === k ? 'bg-brand-gold/10 font-semibold text-brand-gold' : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'} ${disabled ? 'cursor-not-allowed opacity-30' : ''}`}>
                {k}
              </button>
            );
          })}
        </div>
        {trace
          ? <Plot data={trace} layout={mergeLayout({ height: 420, margin: { l: 56, r: 14, t: 8, b: 34 }, showlegend: false, shapes }, theme)} config={PLOTLY_CONFIG} style={{ width: '100%' }} useResizeHandler />
          : <div className="p-12 text-center text-sm text-gray-400">{loading ? 'Loading…' : 'No data in range.'}</div>}
      </div>
    </div>
  );
}

// ── the yield curve: today's term structure + 1M/3M/1Y-ago overlays + 2s10s regime ──────────────
function YieldCurvePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = isDark() ? 'dark' : 'light';
  useEffect(() => {
    atlasService.getCurve().then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);
  const traces = useMemo(() => {
    if (!data?.snapshots?.length) return null;
    // each look-back snapshot gets its own hue so the overlays are tellable apart
    const SNAP_COLORS = theme === 'dark'
      ? { '1M ago': '#60a5fa', '3M ago': '#a78bfa', '1Y ago': '#f87171' }
      : { '1M ago': '#3b82f6', '3M ago': '#8b5cf6', '1Y ago': '#ef4444' };
    return data.snapshots.map((snap) => {
      const today = snap.label === 'Today';
      const color = today ? GOLD : (SNAP_COLORS[snap.label] || (theme === 'dark' ? '#64748b' : '#94a3b8'));
      return {
        x: snap.points.map((p) => p.tenor_label), y: snap.points.map((p) => p.value),
        type: 'scatter', mode: today ? 'lines+markers' : 'lines', name: snap.label,
        line: { color, width: today ? 2.4 : 1.3, dash: today ? 'solid' : 'dot' }, opacity: today ? 1 : 0.75,
        marker: { size: 6, color }, hovertemplate: `${snap.label} %{x}: %{y:.2f}%<extra></extra>`,
      };
    });
  }, [data, theme]);
  const sp = data?.spread_2s10s;
  const regime = sp == null ? null : sp < 0 ? { t: 'INVERTED', c: 'text-red-500' } : sp < 0.1 ? { t: 'FLAT', c: 'text-amber-500' } : { t: 'STEEP', c: 'text-emerald-500' };
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-[#161b22]">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-serif text-base text-gray-900 dark:text-gray-100">US Treasury Yield Curve</div>
          <div className="text-[11px] text-gray-400">{data?.as_of ? `as of ${data.as_of} · ${data.n_tenors} tenors · today vs 1M / 3M / 1Y ago` : 'Loading…'}</div>
        </div>
        {regime && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-gray-400">2s10s slope</div>
            <div className="tabular-nums"><span className="text-gray-800 dark:text-gray-200">{(sp * 100).toFixed(0)} bps</span> <span className={`text-[11px] font-semibold ${regime.c}`}>{regime.t}</span></div>
          </div>
        )}
      </div>
      {traces
        ? <Plot data={traces} layout={mergeLayout({ height: 300, margin: { l: 46, r: 12, t: 8, b: 30 }, showlegend: true, legend: { orientation: 'h', y: -0.2 }, yaxis: { ticksuffix: '%' } }, theme)} config={PLOTLY_CONFIG} style={{ width: '100%' }} useResizeHandler />
        : <div className="p-8 text-center text-sm text-gray-400">{loading ? 'Loading…' : 'Curve unavailable.'}</div>}
    </div>
  );
}

export default function AtlasPage() {
  const [mode, setMode] = useState('regime');       // 'regime' (landing) | 'dashboard' | 'browse'
  const [dash, setDash] = useState(null);
  const [loadingDash, setLoadingDash] = useState(true);
  const [catalog, setCatalog] = useState(null);
  const [sel, setSel] = useState(null);
  const [series, setSeries] = useState(null);
  const [loadingSer, setLoadingSer] = useState(false);
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [q, setQ] = useState('');
  const [modalCode, setModalCode] = useState(null);
  const [modalFreq, setModalFreq] = useState('');

  useEffect(() => {
    atlasService.getDashboard()
      .then((r) => setDash(r.data))
      .catch((e) => setErr(e?.response?.data?.error || 'Failed to load dashboard'))
      .finally(() => setLoadingDash(false));
  }, []);

  // lazy-load the catalog the first time Browse is opened
  useEffect(() => {
    if (mode !== 'browse' || catalog) return;
    atlasService.getCatalog()
      .then((r) => {
        setCatalog(r.data);
        if (!sel) { const b0 = r.data.buckets?.[0]; if (b0?.series?.[0]) setSel(b0.series[0].code); if (b0) setExpanded({ [b0.bucket]: true }); }
      })
      .catch((e) => setErr(e?.response?.data?.error || 'Failed to load catalog'));
  }, [mode, catalog, sel]);

  useEffect(() => {
    if (!sel) return undefined;
    let alive = true;
    setLoadingSer(true);
    atlasService.getSeries(sel)
      .then((r) => { if (alive) setSeries(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.error || 'Failed to load series'); })
      .finally(() => { if (alive) setLoadingSer(false); });
    return () => { alive = false; };
  }, [sel]);

  const openCard = (code, freq) => { setModalCode(code); setModalFreq(freq || ''); };
  const theme = isDark() ? 'dark' : 'light';
  const obs = useMemo(() => series?.observations || [], [series]);
  const chart = useMemo(() => chartTrace(obs, series?.unit_kind), [obs, series]);
  const recessionShapes = useMemo(() => nberShapes(obs, theme), [obs, theme]);

  const buckets = useMemo(() => {
    if (!catalog?.buckets) return [];
    const n = q.trim().toLowerCase();
    if (!n) return catalog.buckets;
    return catalog.buckets
      .map((b) => ({ ...b, series: b.series.filter((s) => s.code.toLowerCase().includes(n) || (s.title || '').toLowerCase().includes(n)) }))
      .filter((b) => b.series.length);
  }, [catalog, q]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-serif text-xl text-gray-900 dark:text-gray-100">Atlas · Macro warehouse</h2>
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            {dash?.newest ? `Signal indicators + full catalog · point-in-time · newest series ${dash.newest}` : 'Loading…'}
          </p>
        </div>
        <div className="inline-flex overflow-hidden rounded-lg border border-gray-300 text-sm dark:border-gray-700">
          {[['regime', 'Regime'], ['dashboard', 'Dashboard'], ['browse', 'Browse']].map(([m, lab]) => (
            <button key={m} onClick={() => setMode(m)}
              className={`px-3 py-1.5 ${mode === m ? 'bg-brand-gold/10 font-semibold text-brand-gold' : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'}`}>{lab}</button>
          ))}
        </div>
      </div>

      {err && <div className="text-sm text-red-500">{err}</div>}

      {/* ── DASHBOARD ── */}
      {mode === 'dashboard' && (
        <div className="space-y-5">
          <YieldCurvePanel />
          {loadingDash && (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-[150px] skeleton rounded-xl" />)}
            </div>
          )}
          {dash?.groups?.map((g) => (
            <div key={g.group}>
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{g.group}</div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                {g.cards.map((c) => <Card key={c.code} c={c} onOpen={openCard} />)}
              </div>
            </div>
          ))}
          {dash && !dash.groups?.length && <div className="text-sm text-gray-400">No indicators yet — derived series may still be computing.</div>}
        </div>
      )}

      {/* ── BROWSE ── */}
      {mode === 'regime' && <MacroRegimePage />}
        {mode === 'browse' && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[280px_1fr]">
          <div className="rounded-xl border border-gray-200 dark:border-gray-800">
            <div className="border-b border-gray-100 p-2 dark:border-gray-800">
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter series…"
                className="w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm dark:border-gray-700 dark:bg-[#161b22]" />
            </div>
            <div className="max-h-[70vh] overflow-y-auto scrollbar-thin p-1">
              {!catalog && <div className="p-3 text-sm text-gray-400">Loading catalog…</div>}
              {buckets.map((b) => {
                const open = expanded[b.bucket] || !!q;
                return (
                  <div key={b.bucket} className="mb-0.5">
                    <button onClick={() => setExpanded((e) => ({ ...e, [b.bucket]: !e[b.bucket] }))}
                      className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800/40">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">{BUCKET_LABEL[b.bucket] || b.bucket}</span>
                      <span className="text-[10px] text-gray-400">{b.count} {open ? '▾' : '▸'}</span>
                    </button>
                    {open && b.series.map((s) => (
                      <button key={s.code} onClick={() => setSel(s.code)}
                        className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-1 text-left ${sel === s.code ? 'bg-brand-gold/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'}`}>
                        <span className="min-w-0">
                          <span className={`block truncate font-mono text-[11px] ${sel === s.code ? 'text-brand-gold' : 'text-gray-700 dark:text-gray-300'}`}>{s.parent_code ? '↳ ' : ''}{s.code}</span>
                          <span className="block truncate text-[10px] text-gray-400">{s.title}</span>
                        </span>
                        <span className="shrink-0 tabular-nums text-[11px] text-gray-500 dark:text-gray-400">{fmtValue(s.latest_value, s.units)}</span>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="min-w-0 space-y-3">
            {!series && <div className="rounded-xl border border-gray-200 p-6 text-sm text-gray-400 dark:border-gray-800">Select a series.</div>}
            {series && (
              <>
                <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-800">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-brand-gold">{series.code}</span>
                        {loadingSer && <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-brand-gold/70 border-t-transparent" />}
                      </div>
                      <h3 className="font-serif text-lg text-gray-900 dark:text-gray-100">{series.title}</h3>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        {series.source === 'derived' && <Badge tone="gold">Derived</Badge>}
                        {series.frequency && <Badge>{series.frequency}</Badge>}
                        {series.units && <Badge>{series.units}</Badge>}
                        {series.seasonal_adj && <Badge>{series.seasonal_adj}</Badge>}
                        {(series.use_tags || '').split(',').filter(Boolean).map((t) => <Badge key={t} tone="gold">{USE_LABEL[t.trim()] || t.trim()}</Badge>)}
                        {series.parent_code && <Badge>↳ of {series.parent_code}</Badge>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wide text-gray-400">Latest</div>
                      <div className="font-serif text-2xl tabular-nums text-gray-900 dark:text-gray-100">{fmtValue(obs.length ? obs[obs.length - 1].value : null, series.units)}</div>
                      <div className="text-[10px] text-gray-400">{series.last}</div>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 p-3 dark:border-gray-800">
                  <div className="mb-1 text-[10px] uppercase tracking-wide text-gray-400">{series.first} → {series.last} · {series.n_obs_dates} obs{series.downsampled ? ' (downsampled)' : ''}</div>
                  {chart ? (
                    <Plot data={chart} layout={mergeLayout({ height: 340, margin: { l: 56, r: 12, t: 8, b: 32 }, showlegend: false, shapes: recessionShapes }, theme)} config={PLOTLY_CONFIG} style={{ width: '100%' }} useResizeHandler />
                  ) : <div className="p-6 text-sm text-gray-400">No observations.</div>}
                </div>

                {series.children?.length > 0 && (
                  <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-800">
                    <div className="mb-2 text-[10px] uppercase tracking-wide text-gray-400">Components ({series.children.length}) · roll up to {series.code}</div>
                    <div className="overflow-x-auto scrollbar-thin">
                      <table className="w-full text-[12px] min-w-[420px]">
                        <thead><tr className="border-b border-gray-100 text-[10px] uppercase tracking-wider text-gray-400 dark:border-gray-800">
                          <th className="px-2 py-1.5 text-left">Series</th><th className="px-2 py-1.5 text-left">Title</th>
                          <th className="px-2 py-1.5 text-right">Weight</th><th className="px-2 py-1.5 text-right">Latest</th>
                        </tr></thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800/60">
                          {series.children.map((c) => (
                            <tr key={c.code} className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/30" onClick={() => setSel(c.code)}>
                              <td className="px-2 py-1.5 font-mono text-brand-gold">{c.code}</td>
                              <td className="max-w-[220px] truncate px-2 py-1.5 text-gray-600 dark:text-gray-300" title={c.title}>{c.title}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums text-gray-500">{c.weight == null ? '—' : c.weight.toFixed(2)}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmtValue(c.last_value, series.units)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {modalCode && <ChartModal code={modalCode} freq={modalFreq} onClose={() => setModalCode(null)} />}
    </div>
  );
}
